import { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { Check, ChevronDown, Search } from "lucide-react";
import api from "../lib/api";

/**
 * Typeahead-Suche für Kunden.
 *
 * Zwei Modi:
 *   1) Client-Filter (Default): `customers` Prop enthält die volle Liste
 *   2) Server-Filter: `serverSearch=true` — feuert `/api/customers?q=X` debounced
 *      und ist damit unabhängig vom Volumen (tausende Kunden möglich).
 *
 * Props:
 *   value: string           Aktuelle customer_id
 *   onChange: (id) => void
 *   customers?: []          Bei Client-Filter nötig, bei Server-Filter Fallback
 *   serverSearch?: bool
 *   placeholder?, testId?
 */
export default function CustomerAutocomplete({
  value, onChange, customers = [], serverSearch = false,
  placeholder = "Kunde suchen…", testId = "customer-autocomplete",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selCache, setSelCache] = useState(null);
  const ref = useRef(null);
  const timer = useRef(null);

  const selected = customers.find((c) => c.id === value) || selCache;

  useEffect(() => {
    // Falls value gesetzt aber Kunde nicht in `customers` und wir Server-Filter nutzen:
    // hole Detail-Info für Label
    if (serverSearch && value && !selected) {
      api.get(`/customers/${value}`).then((r) => setSelCache(r.data)).catch(() => {});
    }
  }, [value, serverSearch, selected]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Server-Filter debounced (250 ms)
  useEffect(() => {
    if (!serverSearch || !open) return;
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get("/customers", { params: { q: q || undefined, page_size: 15 } });
        setRemote(r.data || []);
      } catch { setRemote([]); }
      finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, serverSearch]);

  const results = serverSearch
    ? remote
    : (q.trim().length === 0
        ? customers.slice(0, 12)
        : customers.filter((c) => {
            const s = q.toLowerCase();
            return (c.name || "").toLowerCase().includes(s)
                || (c.email || "").toLowerCase().includes(s)
                || (c.company || "").toLowerCase().includes(s)
                || (c.customer_number || "").toString().toLowerCase().includes(s)
                || (c.zip || "").toString().includes(s);
          }).slice(0, 12));

  const pick = (c) => { setSelCache(c); onChange(c.id); setQ(""); setOpen(false); };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              data-testid={`${testId}-trigger`}
              className="w-full h-10 px-3 pr-10 rounded-lg border border-[var(--border)] bg-white text-left flex items-center justify-between text-sm hover:border-[var(--pop-pink)] transition-colors">
        <span className={`min-w-0 flex-1 truncate flex items-baseline gap-2 ${selected ? "" : "text-[var(--text-muted)]"}`}>
          {selected ? (
            <>
              {selected.customer_number && (
                <span className="font-mono text-[11px] text-[var(--text-muted)] shrink-0">{selected.customer_number}</span>
              )}
              <span className="truncate">{selected.name}</span>
            </>
          ) : placeholder}
        </span>
        <ChevronDown size={16} className="text-[var(--text-muted)] flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[var(--border)] rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="relative border-b border-[var(--border)]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input autoFocus placeholder={serverSearch ? "Tippen zum Suchen…" : `${customers.length} Kunden — tippen zum Filtern…`}
                   value={q}
                   data-testid={`${testId}-search`}
                   onChange={(e) => { setQ(e.target.value); setHi(0); }}
                   onKeyDown={(e) => {
                     if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
                     else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
                     else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); }
                     else if (e.key === "Escape") setOpen(false);
                   }}
                   className="pl-8 border-0 rounded-none focus-visible:ring-0" />
          </div>
          <div className="overflow-y-auto flex-1">
            {loading && <div className="p-3 text-center text-xs text-[var(--text-muted)]">suche…</div>}
            {!loading && results.length === 0 && <div className="p-4 text-center text-sm text-[var(--text-muted)]">Keine Treffer</div>}
            {results.map((c, i) => (
              <button key={c.id} type="button"
                      onClick={() => pick(c)}
                      onMouseEnter={() => setHi(i)}
                      data-testid={`${testId}-opt-${c.id}`}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-[var(--pop-cream)]/60 ${i === hi ? "bg-[var(--pop-cream)]/40" : ""}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-baseline gap-2">
                    {c.customer_number && (
                      <span className="font-mono text-[11px] text-[var(--text-muted)] shrink-0">{c.customer_number}</span>
                    )}
                    <span className="truncate">{c.name}</span>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] truncate font-mono">
                    {[c.email, c.city, c.zip].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                {value === c.id && <Check size={14} className="text-[var(--pop-pink)] flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
